import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { checkDuplicatePhoneCount, fetchNewOrders, updateOrderStatus, deleteOrder, fetchNewOrdersTotals, fetchNewOrdersSummary } from "../../api/OrderAPIs";
import { checkReturnMatch } from "../../api/ReturnsAPIs";
import { fetchDesignById } from "../../api/DesignAPIs";
import { fetchTypeById } from "../../api/TypeAPIs";
import { useAuth } from "../../utils/auth";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL_V2 } from "../../api/URL_Header";
const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  } else if (type === "card") {
    return (
      <div className="animate-pulse bg-white shadow-md rounded-lg p-6 mb-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  } else if (type === "productsCard") {
    return (
      <div className="animate-pulse bg-white shadow-md rounded-lg p-6 mb-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-6 bg-gray-200 rounded w-3/4"></div>
      </div>
    );
  }
  return null;
};

const NewOrdersPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [loadedOrders, setLoadedOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [currentReference, setCurrentReference] = useState("Girabatcom.4000");
  const [referenceInput, setReferenceInput] = useState("Girabatcom.4000");

  const { data: totals, isLoading: isLoadingTotals, error: errorTotals } = useQuery({
    queryKey: ["newOrdersTotals"],
    queryFn: fetchNewOrdersTotals,
    enabled: authState.role === "Admin",
    staleTime: Infinity,
    placeholderData: { totalProfit: 0, totalAmount: 0, totalProductsSold: 0 },
  });

  const { data: newOrdersSummary, isLoading: isLoadingSummary, error: errorSummary } = useQuery({
    queryKey: ["newOrdersSummary"],
    queryFn: fetchNewOrdersSummary,
    enabled: authState.role === "Admin",
    staleTime: Infinity,
    placeholderData: { count: 0, totalProfit: 0, totalAmount: 0 },
  });

  useEffect(() => {
    if (authState.role !== "Admin" && authState.role !== "Moderator") {
      navigate("/login");
      return;
    }
    loadInitialOrders();
  }, [authState, navigate]);

  const isLoading = isLoadingTotals || isLoadingSummary;
  const queryError = errorTotals || errorSummary;

  const handleDownload = async (url, filename = "design.jpg") => {
    try {
      const response = await fetch(
        `${API_BASE_URL_V2}/api/files/download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
      alert("فشل التحميل. تحقق من السيرفر.");
    }
  };
  const copyToClipboard = (text) => {
    // إذا كان المتصفح يدعم clipboard API (مثل Chrome على الكمبيوتر أو HTTPS)
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).then(() => {
        toast.success("تم النسخ بنجاح!");
      }).catch(() => {
        fallbackCopy(text);
      });
    } else {
      // بديل للموبايل والبيئات غير الآمنة
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    textArea.style.left = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        toast.success("تم النسخ بنجاح!");
      } else {
        toast.error("فشل النسخ، حاول مرة أخرى.");
      }
    } catch (err) {
      toast.error("فشل النسخ، حاول النسخ يدويًا.");
      console.error("Copy failed:", err);
    }

    document.body.removeChild(textArea);
  };
  const enrichOrders = async (data) => {
    return await Promise.all(
      data.map(async (order) => {
        let enrichedItems = [];

        for (const detail of order.orderDetails) {
          const design = await fetchDesignById(detail.designId);
          const type = await fetchTypeById(detail.typeId);
          if (!design || !type) continue;

          const baseItem = {
            id: detail.designId,
            name: design.Name,
            price: detail.price,
            type: type.name,
            driveLink: design.code.startsWith("CUSTOM") ? `${API_BASE_URL_V2}${design.driveLink}` : design.driveLink || "",
            code: design.code || "None",
          };

          // كرر المنتج حسب الكمية
          for (let i = 0; i < detail.quantity; i++) {
            enrichedItems.push({
              ...baseItem,
              quantityIndex: i + 1, // عشان نعرف هو الكام في الترتيب
              totalQuantity: detail.quantity,
            });
          }
        }
        // const allReturns = [];
        // for (const item of enrichedItems) {
        //   if (item.type && item.driveLink) {
        //     try {
        //       const matches = await checkReturnMatch(item.type, item.driveLink);
        //       matches.forEach((match) => {
        //         allReturns.push(`${match.orderRef} + ${match.item}`);
        //       });
        //     } catch (err) {
        //       console.error(`Error checking returns for item ${item.id}:`, err);
        //     }
        //   }
        // }
        // const returnsText = allReturns.length > 0 ? allReturns.join(', ') : 'لا يوجد';

        return {
          orderNumber: order.orderId,
          items: enrichedItems,
          total: order.totalAmount,
          profit: order.profit || 0,
          date: new Date(order.date).toISOString().split("T")[0],
          address: order.address?.fullAddress || "Unknown Address",
          state: order.address?.state || "Unknown Address",
          phoneNumber: order.phoneNumber || "Unknown Phone",
          phoneNumber2: order.phoneNumber2 || "Unknown Phone",
          name: order.name || "Unknown Customer",
          note: order.note || "",
          status: order.status || "New",
          // returnsText: returnsText, // NEW: Added for the new column

        };
      })
    );
  };

  const loadInitialOrders = async () => {
    setIsOrdersLoading(true);
    try {
      const data = await fetchNewOrders(0, 10);
      if (data.length === 0 || data.length < 10) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setCurrentPage(1);
    } catch (err) {
      console.error("Error loading initial orders:", err);
      setError("فشل في تحميل الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل الطلبات!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const loadMoreOrders = async () => {
    setIsLoadingMore(true);
    try {
      const data = await fetchNewOrders(currentPage, 10);
      if (data.length === 0 || data.length < 10) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders((prev) => [...prev, ...enrichedOrders]);
      setCurrentPage((prev) => prev + 1);
    } catch (err) {
      console.error("Error loading more orders:", err);
      setError("فشل في تحميل المزيد من الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل المزيد من الطلبات!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsLoadingMore(false);
    }
  };

const handleUpdateStatus = async (orderId, newStatus, order) => {
  // نعرّف reason في البداية عشان نضمن إنه موجود دايمًا
  let reason = "no reason (normal order)";

  // حالة خاصة: تحديث إلى "قديم" مع Reference + فحص التكرار
  if (newStatus === "Old") {
    try {
      // 2. نسخ البيانات مع الرقم المرجعي
      const phone = `${order.phoneNumber}${order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" ? "-" + order.phoneNumber2 : ""}`;
      const formattedState = `${order.state} (C)`;
      // const text = `${currentReference}\t${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD`;

      // copyToClipboard(text);

      // 3. زيادة الرقم المرجعي
      incrementReference();

      // 4. تأكيد التحديث
      const confirmUpdate = window.confirm("هل تريد تحديث الطلب إلى قديم؟");
      if (!confirmUpdate) return;

      // 5. تحديث الحالة في السيرفر
      await updateOrderStatus(orderId, newStatus, reason);

      // 6. إزالة الطلب من القائمة
      setLoadedOrders(prev => prev.filter(o => o.orderNumber !== orderId));
      toast.success("تم النسخ!");

    } catch (err) {
      console.error(err);
      toast.error("فشل في العملية: " + (err.message || "خطأ غير معروف"));
    }
    return; // مهم: نطلع من الدالة هنا
  }

  // الحالات الأخرى (Hold, Changes, Confirmed, إلخ)
  if (newStatus === "Hold") {
    reason = window.prompt("سبب وضع الطلب على الانتظار:");
    if (reason === null) return;
    if (!reason.trim()) {
      toast.error("يجب إدخال سبب!");
      return;
    }
  } else if (newStatus === "Changes") {
    reason = window.prompt("سبب التغيير:");
    if (reason === null) return;
    if (!reason.trim()) {
      toast.error("يجب إدخال سبب!");
      return;
    }
  }
  else if (newStatus === "Spam") {
      reason = window.prompt("سبب التغيير:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    }

  // تأكيد عام لكل الحالات (ما عدا Old اللي اتعمل له تأكيد فوق)
  const confirm = window.confirm(`تأكيد تحديث الحالة إلى "${newStatus}"؟`);
  if (!confirm) return;

  try {
    await updateOrderStatus(orderId, newStatus, reason);
    setLoadedOrders(prev => prev.filter(o => o.orderNumber !== orderId));
    toast.success(`تم تحديث الطلب إلى ${newStatus} بنجاح`);
  } catch (err) {
    toast.error("فشل التحديث: " + err.message);
  }
};

  const handleDeleteOrder = async (orderId) => {
    if (authState.role !== "Admin" && authState.role !== "Moderator") return;
    const confirmDelete = window.confirm(`هل أنت متأكد من حذف الطلب رقم ${orderId}؟`);
    if (!confirmDelete) return;
    try {
      await deleteOrder(orderId);
      setLoadedOrders((prev) =>
        prev.filter((order) => order.orderNumber !== orderId)
      );
      toast.success(`تم حذف الطلب رقم ${orderId} بنجاح!`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (err) {
      console.error("Error deleting order:", err);
      let errorMessage = "فشل في حذف الطلب. يرجى المحاولة مرة أخرى.";
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handleCopyMessage = (order) => {
    const itemsList = order.items.map(item => `النوع: ${item.type} - الكود: ${item.code}`).join('\n');

    const messages = [
      `أهلاً أ/ "${order.name}" 🌸
  مع حضرتك فاطمة من فريق جرابتكم ستور 💛
  بنراجع معاك تفاصيل الطلب قبل ما نبدأ التنفيذ، لأن كل تصميم بيتعمل مخصوص لك 🎨✨

  تفاصيل الطلب:
  ${itemsList}

  بمجرد تأكيدك هنبدأ فورًا في التجهيز والشحن ⚡🚚

  لتأكيد الطلب: رد بـ (نعم - تأكيد) ✅
  لو في تعديل أو إلغاء، قولنا قبل البدء 💬
  شكرًا لثقتك فينا 🤍🌷`,

      `مرحبًا أ/ "${order.name}" 🌼
  أنا فاطمة من جرابتكم ستور 💖
  قبل ما نبدأ تنفيذ الطلب، بنحب نأكد معاك التفاصيل عشان كل حاجة تطلع زي ما تحب 🎯

  المنتجات المطلوبة:
  ${itemsList}

  لما تؤكد، هنشتغل عليه فورًا ونجهز الشحن 🚛
  رد بـ (نعم - تأكيد) لو كل حاجة تمام ✅
  لو في أي تعديل، احنا تحت أمرك قبل التنفيذ ✏️
  متشكرين لاختيارك جرابتكم 💝`,

      `أهلاً وسهلاً أ/ "${order.name}" 💐
  معاكِ فاطمة من فريق جرابتكم ستور ✨
  بنمر على تفاصيل الطلب معاك قبل التنفيذ، لأن التصميمات بتتعمل خصيصًا لك 🎨

  الطلب يحتوي على:
  ${itemsList}

  بمجرد ما ترد بـ (نعم - تأكيد)، هنبدأ التنفيذ فورًا ✅
  لو عايز تعديل أو إلغاء، قولنا قبل ما نبدأ 💬
  شكرًا لتفضيلك جرابتكم 🤍`,

      `مرحبًا أ/ "${order.name}" 🌟
  أنا فاطمة من جرابتكم ستور 💛
  قبل ما ننفذ الطلب، بنحب نتأكد من كل التفاصيل معاك عشان تطلع المنتجات مثالية 🎯

  تفاصيل الطلب:
  ${itemsList}

  لو كل حاجة تمام، رد بـ (نعم - تأكيد) وهنبدأ على طول ⚡
  في حالة التعديل أو الإلغاء، أبلغنا قبل البدء ✋
  متشكرين لاختيارك لينا 🌸`,

      `أهلاً أ/ "${order.name}" 🌺
  مع حضرتك فاطمة من جرابتكم ستور 💖
  قبل تنفيذ الطلب، بنراجع معاك التفاصيل لأن كل قطعة بتتعمل خصيصًا لك 🎨

  المنتجات:
  ${itemsList}

  بمجرد تأكيدك بـ (نعم - تأكيد)، هنبدأ التجهيز والشحن فورًا 🚚
  لو في تعديل، قولنا قبل البدء 💬
  شكرًا لثقتك فينا 🤍`,

      `مرحبًا أ/ "${order.name}" 💫
  أنا فاطمة من فريق جرابتكم ستور 🌷
  بنحب نأكد معاك تفاصيل الطلب قبل التنفيذ، عشان كل حاجة تطلع زي ما تتمنى 🎨

  الطلب يشمل:
  ${itemsList}

  رد بـ (نعم - تأكيد) لو كل شيء صحيح ✅
  لو في تعديل أو إلغاء، أخبرنا قبل البدء ✏️
  متشكرين لاختيارك جرابتكم 💛`,

      `أهلاً أ/ "${order.name}" 🌸
  معاك فاطمة من جرابتكم ستور ✨
  قبل ما نبدأ تنفيذ الطلب، بنراجع معاك التفاصيل لأن التصميمات بتتعمل خصيصًا 🎯

  تفاصيل المنتجات:
  ${itemsList}

  بمجرد ردك بـ (نعم - تأكيد)، هنشتغل عليه فورًا ⚡
  لو عايز تعديل، قولنا قبل التنفيذ 💬
  شكرًا لاختيارك لينا 🤍`
    ];

    // اختيار رسالة عشوائية
    const randomMessage = messages[Math.floor(Math.random() * messages.length)];

    copyToClipboard(randomMessage);
  };

  // رسالة بوسطة الثابتة
  const handleCopyBostaMessage = () => {
    const message = `
بنحب نبلغ حضرتك إن شركة الشحن هتتواصل مع حضرتك على الواتساب خلال وقت قصير لتأكيد العنوان واستلام الطلب ✅
تنويه مهم:
في رسالة التأكيد اللي هتوصلك ممكن تلاقي اسم شركة مختلفة (مثل TeePlus) 💼  

كمان عدد القطع ممكن يظهر 1 حتى لو كانت اكتر من قطعة واحدة 🧾
يُرجى الرد على رسالة شركة الشحن علشان يتم تأكيد الطلب ويتم بدء الشحن ✉️⚡  

شكرًا لاختيارك جرابتكم 🌷`;

      copyToClipboard(message);

  };
  // رسالة طلب توضيح العنوان
  const handleCopyAddressClarification = () => {
    const message = `أهلاً بحضرتك

العنوان المسجل غير واضح أو ناقص تفاصيل مهمة لتسليم الطلب بنجاح

برجاء توضيح العنوان بالتفصيل:
• اسم الشارع
• رقم العمارة / الشقة
• معالم قريبة (مثل: بجوار صيدلية، أمام مدرسة، إلخ)

شكرًا لتفهمك، هنبدأ تجهيز الطلب فور توضيح العنوان`;

    copyToClipboard(message);
  };

  const handleCopyAllExcelData = async (order) => {
    try {
      // فحص التكرار
      const count = await checkDuplicatePhoneCount(
        order.orderNumber,
        order.phoneNumber,
        order.phoneNumber2 || ""
      );

      if (count > 0) {
        const proceed = window.confirm(
          `تحذير: هذا العميل لديه ${count} طلب(ات) سابقة بنفس الرقم!\n\nهل تريد الاستمرار في النسخ؟`
        );
        if (!proceed) {
          toast.info("تم إلغاء النسخ بسبب تكرار الرقم");
          return;
        }
      }

      const phone = `${order.phoneNumber}${
        order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" ? "-" + order.phoneNumber2 : ""
      }`;
      const formattedState = `${order.state} (C)`;
      const customerBase = `${currentReference}\t\t${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD\t`;

      const items = order.items; // دلوقتي فيها التكرار (مثلاً 3 عناصر لو الكمية 3)
      const lines = [];

      items.forEach((item, index) => {
        const itemNum = `${index + 1}-${items.length}`; // 1-3 , 2-3 , 3-3
        const type = item.type || "Unknown Type";
        const driveLink = item.driveLink || "No Link";

        const itemData = `\t\tPhone Cover (H)\t${type}\t\t1\t${driveLink}`;
        const itemLine = `${itemNum}${itemData}`;

        if (index === 0) {
          lines.push(`${customerBase}\t\t${itemLine}`);
        } else {
          lines.push(`${currentReference}\t\t\t\t\t\t\t\t\t\t\t${itemLine}`);
        }
      });

      const finalText = lines.join('\n');
      copyToClipboard(finalText);

      // زيادة الريفرنس بعد النسخ (لو عايز)
      // incrementReference();

      toast.success("تم نسخ الطلب كامل مع التكرار بنجاح!");
    } catch (err) {
      console.error("خطأ في النسخ:", err);
      toast.error("فشل في النسخ، حاول مرة تانية");
    }
  };
  // دالة جديدة: نسخ كل الطلبات المعروضة حاليًا مرة واحدة
// دالة جديدة مساعدة: هل الطلب يحتوي على أي عنصر CUSTOM؟
const isOrderNonCustom = (order) => {
  return !order.items.some(item => item.code && item.code.startsWith("CUSTOM"));
};

// ===================================================================
// 1. نسخ جماعي للإكسيل – فقط الطلبات غير الـ CUSTOM
// ===================================================================
const handleCopyAllVisibleOrders = async () => {
  const nonCustomOrders = loadedOrders.filter(isOrderNonCustom);

  if (nonCustomOrders.length === 0) {
    toast.warn("لا توجد طلبات غير مخصصة (CUSTOM) للنسخ");
    return;
  }

  const confirm = window.confirm(
    `هل تريد نسخ ${nonCustomOrders.length} طلب(ات) غير مخصصة مرة واحدة إلى الإكسيل؟\n\n(الطلبات التي تحتوي على تصميم CUSTOM سيتم تخطيها)\n\nسيتم فحص التكرار + زيادة الرقم المرجع تلقائيًا`
  );
  if (!confirm) return;

  let finalLines = [];
  let hasDuplicate = false;
  let currentRef = currentReference;

  for (const order of nonCustomOrders) {
    try {
      const count = await checkDuplicatePhoneCount(
        order.orderNumber,
        order.phoneNumber,
        order.phoneNumber2 || ""
      );

      if (count > 0) {
        hasDuplicate = true;
        finalLines.push(`# تحذير: تكرار رقم (${count} طلب سابق) - ${order.name} - ${order.phoneNumber}`);
      }

      const phone = `${order.phoneNumber}${
        order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" ? "-" + order.phoneNumber2 : ""
      }`;
      const formattedState = `${order.state} (C)`;
      const customerBase = `${currentRef}\t\t${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD\t`;

      const items = order.items;
      const orderLines = [];

      items.forEach((item, index) => {
        const itemNum = `${index + 1}-${items.length}`;
        const type = item.type || "Unknown Type";
        const driveLink = item.driveLink || "No Link";

        const itemData = `\t\tPhone Cover (H)\t${type}\t\t1\t${driveLink}`;
        const itemLine = `${itemNum}${itemData}`;

        if (index === 0) {
          orderLines.push(`${customerBase}\t\t${itemLine}`);
        } else {
          orderLines.push(`${currentRef}\t\t\t\t\t\t\t\t\t\t\t${itemLine}`);
        }
      });

      finalLines.push(...orderLines);

      // زيادة الريفرنس للطلب التالي
      const match = currentRef.match(/(\d+)$/);
      if (match) {
        const num = parseInt(match[0], 10);
        currentRef = currentRef.replace(/\d+$/, (num + 1).toString());
      }
    } catch (err) {
      finalLines.push(`# خطأ في الطلب رقم ${order.orderNumber}`);
    }
  }

  // حفظ الريفرنس الجديد
  setCurrentReference(currentRef);
  setReferenceInput(currentRef);
  localStorage.setItem("orderReference", currentRef);

  const finalText = finalLines.join('\n');
  copyToClipboard(finalText);

  toast.success(
    `تم نسخ ${nonCustomOrders.length} طلب(ات) غير مخصصة بنجاح!${hasDuplicate ? " (يوجد تكرار في بعض الأرقام)" : ""}`,
    { autoClose: 6000 }
  );
};

// ===================================================================
// 2. تحويل الكل إلى قديم – فقط الطلبات غير الـ CUSTOM
// ===================================================================
const handleMarkAllAsOld = async () => {
  const nonCustomOrders = loadedOrders.filter(isOrderNonCustom);

  if (nonCustomOrders.length === 0) {
    toast.warn("لا توجد طلبات غير مخصصة (CUSTOM) لتحويلها إلى قديم");
    return;
  }

  const confirm = window.confirm(
    `هل تريد تحويل ${nonCustomOrders.length} طلب(ات) غير مخصصة إلى "قديم"؟\n\n` +
    `(الطلبات التي تحتوي على تصميم CUSTOM سيتم تخطيها)\n\n` +
    `الطلبات المحددة ستختفي من الصفحة فورًا.`
  );

  if (!confirm) return;

  let successCount = 0;
  let failedCount = 0;

  toast.info("جاري تحديث الطلبات غير المخصصة إلى قديم... لا تغلق الصفحة", {
    autoClose: false,
    toastId: "marking-old",
  });

  for (const order of nonCustomOrders) {
    try {
      await updateOrderStatus(order.orderNumber, "Old", "تم التحديث الجماعي بدون نسخ (غير مخصص)");
      successCount++;
    } catch (err) {
      console.error(`فشل تحديث الطلب ${order.orderNumber}:`, err);
      failedCount++;
    }
  }

  // إزالة فقط الطلبات التي تم تحديثها (غير المخصصة)
  setLoadedOrders(prev => prev.filter(order => !nonCustomOrders.some(o => o.orderNumber === order.orderNumber)));

  toast.dismiss("marking-old");

  toast.success(
    `تم تحويل ${successCount} طلب(ات) غير مخصصة إلى قديم بنجاح!` +
    (failedCount > 0 ? `\nفشل في ${failedCount} طلب(ات)` : ""),
    { autoClose: 6000 }
  );
};
  
  useEffect(() => {
    // جلب آخر رقم مرجع من localStorage عند تحميل الصفحة
    const saved = localStorage.getItem("orderReference");
    if (saved) {
      setCurrentReference(saved);
      setReferenceInput(saved);
    }
  }, []);
  const incrementReference = () => {
    const match = currentReference.match(/(\d+)$/);
    if (match) {
      const num = parseInt(match[0], 10);
      const newNum = num + 1;
      const newRef = currentReference.replace(/\d+$/, newNum.toString());
      setCurrentReference(newRef);
      localStorage.setItem("orderReference", newRef);
    }
  };

  const updateReferenceFromInput = () => {
    if (referenceInput.trim() && referenceInput.includes(".")) {
      setCurrentReference(referenceInput.trim());
      localStorage.setItem("orderReference", referenceInput.trim());
    }
  };
  return (
    <div className="min-h-screen bg-gray-50 p-6" dir="rtl">
      <ToastContainer />
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold">الطلبات الجديدة</h2>
          <button
            onClick={() => navigate("/dashboard")}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            رجوع
          </button>
        </div>

        {(error || queryError) && (
          <div className="bg-red-100 text-red-700 p-4 rounded mb-6">
            {error || "فشل في تحميل بيانات الطلبات الجديدة. يرجى المحاولة مرة أخرى."}
          </div>
        )}

        {authState.role === "Admin" && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white shadow-md rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-2">ملخص الطلبات الجديدة</h2>
                {isLoading ? (
                  <SkeletonLoader type="card" />
                ) : (
                  <div>
                    <p>عدد الطلبات: {newOrdersSummary.count}</p>
                    <p>إجمالي المبلغ: LE {newOrdersSummary.totalAmount}</p>
                  </div>
                )}
              </div>
              <div className="bg-white shadow-md rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-2">إجمالي المنتجات المباعة</h2>

                {isLoading ? (
                  <SkeletonLoader type="productsCard" />
                ) : (
                  <>
                    <p>إجمالي المنتجات المباعة: {totals.totalProductsSold}</p>

                    {/* <p>
                      تكلفة القطع : عددهم * 63 = {totals.totalProductsSold * 63} ج.م
                    </p>

                    <p>بدون الشحن : {newOrdersSummary.totalProfit} ج.م</p>

                    <p>
                      صافي الربح بعد طرح التكلفة:{" "}
                      {newOrdersSummary.totalProfit - totals.totalProductsSold * 63} ج.م
                    </p> */}
                  </>
                )}
              </div>

            </div>
          </>
        )}

        {/* رسالة بوسطة - زر ثابت فوق الجدول */}
        <div className="mb-6 p-5 bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl shadow-sm">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-right">
              <p className="font-bold text-amber-900 text-lg">طلب توضيح العنوان</p>
              <p className="text-sm text-amber-700">
                انسخ الرسالة وأرسلها للعميل إذا كان العنوان غير واضح
              </p>
            </div>
            <button
              onClick={handleCopyAddressClarification}
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold px-6 py-3 rounded-lg shadow-md transition transform hover:scale-105"
            >
              نسخ رسالة توضيح العنوان
            </button>
          </div>
        </div>
        
        <div className="mb-6 p-5 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl shadow-sm">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-right">
              <p className="font-bold text-blue-900 text-lg">رسالة بوسطة (للشحن)</p>
              <p className="text-sm text-blue-700">
                انسخ الرسالة وأرسلها لشركة الشحن عند إعداد الطلبات
              </p>
            </div>
            <button
              onClick={handleCopyBostaMessage}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-3 rounded-lg shadow-md transition transform hover:scale-105"
            >
              نسخ رسالة بوسطة
            </button>
          </div>
        </div>
        {/* زر نسخ الكل مرة واحدة - أضفه تحت إعداد الـ Reference وقبل الجدول */}
        <div className="mb-6 p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl shadow-lg">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-right">
              <p className="font-bold text-green-900 text-xl">نسخ جماعي للإكسيل</p>
              <p className="text-sm text-green-700">
                نسخ كل الطلبات المعروضة حاليًا (حتى {loadedOrders.length} طلب) مرة واحدة<br />
                مع فحص التكرار + زيادة الرقم المرجمع تلقائيًا
              </p>
            </div>
            <button
              onClick={handleCopyAllVisibleOrders}
              disabled={isOrdersLoading || loadedOrders.length === 0}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold text-lg px-8 py-4 rounded-xl shadow-lg transition transform hover:scale-105 disabled:cursor-not-allowed"
            >
              نسخ الكل مرة واحدة ({loadedOrders.length})
            </button>
          </div>
        </div>
        {/* زر تحديث الكل إلى قديم مرة واحدة */}
        <div className="mb-6 p-6 bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-300 rounded-xl shadow-lg">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-right">
              <p className="font-bold text-purple-900 text-xl">تحويل الكل إلى قديم (تنظيف سريع)</p>
              <p className="text-sm text-purple-700">
                يحول كل الطلبات المعروضة إلى "قديم" ويزيلها من الصفحة فورًا<br />
                بدون نسخ · بدون فحص تكرار · بدون تغيير الرقم المرجعي
              </p>
            </div>
            <button
              onClick={handleMarkAllAsOld}
              disabled={isOrdersLoading || loadedOrders.length === 0}
              className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-bold text-lg px-8 py-4 rounded-xl shadow-lg transition transform hover:scale-105 disabled:cursor-not-allowed"
            >
              تحويل الكل إلى قديم فقط ({loadedOrders.length})
            </button>
          </div>
        </div>
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <h2 className="text-xl font-bold mb-4 text-right">إعداد رقم المرجع (Reference)</h2>
          <div className="flex items-center gap-4 justify-end flex-wrap">
            <div className="text-right">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                الرقم الحالي (سيظهر في النسخ):
              </label>
              <div className="text-2xl font-bold text-green-600">
                {currentReference}
              </div>
            </div>
            <div className="text-right">
              <label htmlFor="baseRef" className="block text-sm font-medium text-gray-700 mb-1">
                ابدأ من (مثال: Girabatcom.150)
              </label>
              <input
                id="baseRef"
                type="text"
                value={referenceInput}
                onChange={(e) => setReferenceInput(e.target.value)}
                onBlur={updateReferenceFromInput}
                className="border border-gray-300 rounded px-4 py-2 text-right w-64"
                placeholder="Girabatcom.100"
              />
            </div>
            <button
              onClick={() => {
                if (referenceInput.trim()) {
                  setCurrentReference(referenceInput.trim());
                  localStorage.setItem("orderReference", referenceInput.trim());
                  toast.success("تم تحديث رقم المرجع!");
                }
              }}
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
            >
              تحديث الرقم
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-2 text-right">
            كل ما تنسخ بيانات عميل بعد "تحديث إلى قديم"، الرقم سيزيد تلقائيًا
          </p>
        </div>

        <div className="bg-white shadow-md rounded-lg p-6">
          {isOrdersLoading ? (
            <SkeletonLoader type="table" />
          ) : (
            <>
              {loadedOrders.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full table-auto">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="p-1 text-right font-semibold">رقم الطلب</th>
                          <th className="p-1 text-right font-semibold">اسم العميل</th>
                          <th className="p-1 text-right font-semibold">رقم الهاتف</th>
                          <th className="p-1 text-right font-semibold">العنوان</th>
                          <th className="p-1 text-right font-semibold">محافظه</th>
                          <th className="p-1 text-right font-semibold">الإجمالي</th>
                          <th className="p-1 text-right font-semibold">رقم</th>
                          <th className="p-1 text-right font-semibold">المنتجات</th>
                          <th className="p-1 text-right font-semibold">رابط الدرايف</th>
                          <th className="p-1 text-right font-semibold">ملاحظات</th>
                          {authState.role === "Admin" && <th className="p-1 text-right font-semibold min-w-[150px]">نسخ Excel</th>} 
                          <th className="p-1 text-right font-semibold min-w-[200px]">تأكيد</th>
                          <th className="p-1 text-right font-semibold min-w-[120px]">إجراءات</th>
                          {/*{authState.role === "Admin" && <th className="p-1 text-right font-semibold">مرتجع</th>}*/}

                          <th className="p-1 text-right font-semibold min-w-[80px]">حذف</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadedOrders.map((order) => (
                          <tr key={order.orderNumber} className="border-b hover:bg-gray-100">
                            <td className="p-2 text-right">{order.orderNumber}</td>
                            <td className="p-2 text-right">{order.name}</td>
                            <td className="p-2 text-right">
                              <a
                                href={`https://wa.me/${order.phoneNumber.replace(/[^0-9]/g, '').replace(/^0/, '20')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-green-600 hover:text-green-800 font-medium underline"
                              >
                                {order.phoneNumber}
                              </a>
                              {order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" && (
                                <>
                                  <br />
                                  <a
                                    href={`https://wa.me/${order.phoneNumber2.replace(/[^0-9]/g, '').replace(/^0/, '20')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green-600 hover:text-green-800 font-medium underline text-sm"
                                  >
                                    {order.phoneNumber2}
                                  </a>
                                </>
                              )}
                            </td>                            
                            <td className="p-2 text-right">{order.address}</td>
                            <td className="p-2 text-right">{order.state}</td>
                            <td className="p-2 text-right">{order.total}</td>
                            <td className="p-2 text-right">
                              {order.items.map((item, index) => (
                                <div key={`${item.id}-${index}`} className="mb-2">
                                  {index + 1}-{order.items.length}
                                </div>
                              ))}
                            </td>
                            <td className="p-2 text-right">
                              {order.items.map((item) => (
                                <div key={item.id} className="mb-2">
                                  {item.name} {item.type}
                                  <p>----</p>
                                </div>
                              ))}
                            </td>
                            <td className="p-2 text-right">
                              {order.items.map((item) => (
                                <div key={item.id} className="mb-2">
                                  {item.driveLink ? (
                                    <>
                                      <a
                                        href={item.driveLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-500 hover:underline"
                                      >
                                        رابط الدرايف
                                      </a>
                                      <br />
                                      <span>{item.code}</span>
                                      {item.code.startsWith("CUSTOM") && (
                                        <>
                                          <button
                                            onClick={() => handleDownload(item.driveLink, `${order.orderNumber}.jpg`)}
                                            className="ml-2 bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600 text-xs"
                                          >
                                            تحميل
                                          </button>
                                          <br />
                                        </>
                                      )}
                                    </>
                                  ) : (
                                    "لا يوجد رابط"
                                  )}
                                  <p>----</p>
                                </div>
                              ))}
                            </td>
                            <td className="p-2 text-right">{order.note || "لا توجد ملاحظات"}</td>
                            {authState.role === "Admin" && (
                              <td className="p-2 text-right">
                                <div className="flex flex-col space-y-1">
                                <button
                                  onClick={() => handleCopyAllExcelData(order)}
                                  className="bg-green-500 text-white rounded hover:bg-green-600 text-sm px-3 py-8 font-medium"
                                >
                                  نسخ المعلومات 
                                </button>
                                {/* <button
                                  onClick={() => handleCopyItems(order)}
                                  className="bg-green-500 text-white rounded hover:bg-green-600 text-sm px-3 py-2 font-medium"

                                >
                                  نسخ المنتجات الي excel
                                </button> */}
                                </div>
                              </td>
                            )}
                            <td className="p-2 text-right">
                              <div className="flex flex-col space-y-1">
                                <button
                                  onClick={() => handleUpdateStatus(order.orderNumber, "Old", order)}
                                  className="bg-gray-600 text-white px-4 py-5 rounded hover:bg-gray-700 text-sm font-bold"
                                >
                                  تحديث إلى قديم
                                </button>
                                <button
                                  onClick={() => handleUpdateStatus(order.orderNumber, "Confirmed")}
                                  className="bg-green-500 text-white rounded hover:bg-green-600 text-sm px-2 py-2 font-medium"
                                >
                                  تأكيد
                                </button>
                                <button
                                  onClick={() => handleCopyMessage(order)}
                                  className="bg-gray-500 text-white rounded hover:bg-gray-600 text-sm px-2 py-2 font-medium"
                                >
                                  نسخ رسالة
                                </button>
                              </div>
                            </td>
                            <td className="p-2 text-right">
                              <div className="flex flex-col space-y-1">
                                <button
                                  onClick={() => handleUpdateStatus(order.orderNumber, "Old", order)}
                                  className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 text-sm font-bold"
                                >
                                  تحديث إلى قديم
                                </button>
                                <button
                                  onClick={() => handleUpdateStatus(order.orderNumber, "Hold")}
                                  className="bg-yellow-500 text-white rounded hover:bg-yellow-600 text-sm px-3 py-2 font-medium"
                                >
                                   على الانتظار
                                </button>
                                <button
                                  onClick={() => handleUpdateStatus(order.orderNumber, "Changes")}
                                  className="bg-purple-500 text-white rounded hover:bg-purple-600 text-sm px-3 py-2 font-medium"
                                >
                                  تحديث إلى تغييرات
                                </button>
                                <button
                                  onClick={() => handleUpdateStatus(order.orderNumber, "Under Purchase")}
                                  className="bg-indigo-500 text-white rounded hover:bg-indigo-600 text-sm px-3 py-2 font-medium"
                                >
                                  تحت الشراء
                                </button>
                                 <button
                                  onClick={() => handleUpdateStatus(order.orderNumber, "Deposit Required")}
                                  className="bg-orange-500 text-white rounded hover:bg-orange-600 text-sm px-3 py-2 font-medium"
                                >
                                 ديبوزت
                                </button>
                              </div>
                            </td>
                            {/* {authState.role === "Admin" && (
                              <td className="p-2 text-right">
                                {order.returnsText}
                              </td>
                            )} */}
                            <td className="p-2 text-right">
                              {(authState.role === "Admin" || authState.role === "Moderator") && (
                                <div className="flex flex-col space-y-1">
                                  <button
                                    onClick={() => handleDeleteOrder(order.orderNumber)}
                                    className="bg-red-500 text-white rounded hover:bg-red-600 text-sm px-3 py-2 font-medium w-full"
                                  >
                                    حذف
                                  </button>
                                  <button
                                    onClick={() => handleUpdateStatus(order.orderNumber, "Spam")}
                                    className="bg-red-500 text-white rounded hover:bg-red-600 text-sm px-3 py-2 font-medium"
                                  >
                                    معندهوش واتساب او مردش - Spam
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {hasMore && (
                      <div className="text-center mt-4">
                        <button
                          onClick={loadMoreOrders}
                          disabled={isLoadingMore}
                          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
                        >
                          {isLoadingMore ? "جاري التحميل..." : "تحميل المزيد"}
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-center">لا توجد طلبات جديدة</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewOrdersPage;